import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BusRoutePage = () => {
    const sessionId = localStorage.getItem("currentSessionId"); // current session

    const [busRoutes, setBusRoutes] = useState([]);
    const [routeName, setRouteName] = useState('');
    const [location, setLocation] = useState('');
    const [selectedRoute, setSelectedRoute] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        if (!sessionId) {
            showMessage('error', 'Please select a session first.');
            return;
        }
        fetchBusRoutes();
    }, [sessionId]);

    const showMessage = (type, text) => {
        setMessage({ type, text });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    };

    // Fetch bus routes
   const fetchBusRoutes = async () => {
    try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/busroutes/getBusRoutes`, {
            params: { sessionId }
        });

        // Log the response to check if it contains routes
        console.log('Fetched Routes:', response.data); // Add this log to see the response

        // Filter locations by session
        const sessionRoutes = response.data.map(route => ({
            ...route,
            locations: route.locations.filter(loc => loc.session === sessionId || loc.session?._id === sessionId)
        }));

        setBusRoutes(sessionRoutes); // Set the state to display routes
    } catch (error) {
        console.error('Error fetching bus routes:', error);
        showMessage('error', 'Failed to fetch bus routes.');
    }
};


    // Add a new bus route
    const handleRouteSubmit = async (e) => {
        e.preventDefault();
        if (!sessionId) return showMessage('error', 'Session not selected');

        setLoading(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/busroutes/createBusRoute`, {
                routeName,
                sessionId,
            }
            , { headers:{ Authorization: `Bearer ${localStorage.getItem("token")}` } }  
        );

            setBusRoutes([...busRoutes, { ...response.data, locations: [] }]);
            setRouteName('');
            showMessage('success', 'Bus route added successfully!');
        } catch (error) {
            console.error('Error adding bus route:', error);
            showMessage('error', error?.response?.data?.message || 'Failed to add bus route.');
        } finally {
            setLoading(false);
        }
    };

    // Add a new location
    const handleLocationSubmit = async (e) => {
        e.preventDefault();
        if (!selectedRoute) return showMessage('error', 'Please select a route.');
        if (!sessionId) return showMessage('error', 'Session not selected');

        setLoading(true);
        try {
            const response = await axios.post(
                ` ${process.env.REACT_APP_API_BASE_URL}/api/locations/${selectedRoute}/createBusLocation`,
                { location, sessionId },
                { headers:{ Authorization: `Bearer ${localStorage.getItem("token")}` } }
            );

            const updatedRoutes = busRoutes.map(route => {
                if (route._id === selectedRoute) {
                    return {
                        ...route,
                        locations: [...route.locations, response.data]
                    };
                }
                return route;
            });

            setBusRoutes(updatedRoutes);
            setLocation('');
            showMessage('success', 'Location added successfully!');
        } catch (error) {
            console.error('Error adding location:', error);
            showMessage('error', error?.response?.data?.message || 'Failed to add location.');
        } finally {
            setLoading(false);
        }
    };

    // Delete bus route
    const handleDeleteRoute = async (id) => {
        if (!window.confirm('Are you sure you want to delete this bus route?')) return;
        if (!sessionId) return showMessage('error', 'Session not selected');

        try {
            await axios.delete(` ${process.env.REACT_APP_API_BASE_URL}/api/busroutes/deleteBusRoute/${id}`, { data: { sessionId }, headers:{ Authorization: `Bearer ${localStorage.getItem("token")}` } });
            setBusRoutes(busRoutes.filter(route => route._id !== id));
            showMessage('success', 'Bus route deleted successfully!');
        } catch (error) {
            console.error('Error deleting bus route:', error);
            showMessage('error', error?.response?.data?.message || 'Failed to delete bus route.');
        }
    };

    // Delete a location
    const handleDeleteLocation = async (routeId, locName) => {
        if (!window.confirm('Are you sure you want to delete this location?')) return;
        if (!sessionId) return showMessage('error', 'Session not selected');

        try {
            await axios.delete(
                ` ${process.env.REACT_APP_API_BASE_URL}/api/locations/${routeId}/deleteLocation/${locName}`,
                { params: { sessionId }, headers:{ Authorization: `Bearer ${localStorage.getItem("token")}` } }
            );

            const updatedRoutes = busRoutes.map(route => {
                if (route._id === routeId) {
                    return {
                        ...route,
                        locations: route.locations.filter(loc => loc.location !== locName)
                    };
                }
                return route;
            });

            setBusRoutes(updatedRoutes);
            showMessage('success', 'Location deleted successfully!');
        } catch (error) {
            console.error('Error deleting location:', error);
            showMessage('error', error?.response?.data?.message || 'Failed to delete location.');
        }
    };

    return (
        <div className="container mx-auto p-4">
            <h1 className="text-2xl font-bold mb-4 text-blue-800">Bus Routes Management</h1>

            {message.text && (
                <p className={`mb-4 text-sm ${message.type === 'error' ? 'text-red-500' : 'text-green-600'}`}>
                    {message.text}
                </p>
            )}

            {/* Add Bus Route */}
            <form onSubmit={handleRouteSubmit} className="mb-6">
                <div className="mb-2">
                    <label className="block text-gray-700">Route Name:</label>
                    <input
                        type="text"
                        value={routeName}
                        onChange={(e) => setRouteName(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded"
                        placeholder="Enter Route Name"
                        required
                    />
                </div>
                <button
                    type="submit"
                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-700"
                    disabled={loading}
                >
                    {loading ? 'Saving...' : 'Add Bus Route'}
                </button>
            </form>

            {/* Add Location */}
            <div className="mb-6">
                <h2 className="text-xl font-semibold mb-2">Add Location to Route</h2>
                <form onSubmit={handleLocationSubmit}>
                    <div className="mb-2">
                        <label className="block text-gray-700">Select Route:</label>
                        <select
                            value={selectedRoute}
                            onChange={(e) => setSelectedRoute(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded"
                            required
                        >
                            <option value="">-- Select Route --</option>
                            {busRoutes.map(route => (
                                <option key={route._id} value={route._id}>
                                    {route.routeName}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="mb-2">
                        <label className="block text-gray-700">Location:</label>
                        <input
                            type="text"
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded"
                            placeholder="Enter Location"
                            required
                        />
                    </div>
                    <button
                        type="submit"
                        className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-700"
                        disabled={loading}
                    >
                        {loading ? 'Saving...' : 'Add Location'}
                    </button>
                </form>
            </div>

            {/* Bus Routes Table */}
            <div>
                <h2 className="text-xl font-semibold mb-2">Existing Bus Routes</h2>
                <table className="min-w-full bg-gray-300 border border-gray-300">
                    <thead>
                        <tr>
                            <th className="py-2 px-4 border-b">Sr No.</th>
                            <th className="py-2 px-4 border-b">Route Name</th>
                            <th className="py-2 px-4 border-b">Locations</th>
                            <th className="py-2 px-4 border-b">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-gray-100">
                        {busRoutes.map((route, index) => (
                            <tr key={route._id} className="hover:bg-gray-200 transition-colors duration-300">
                                <td className="py-2 px-4 border-b text-center">{index + 1}</td>
                                <td className="py-2 px-4 border-b text-center">{route.routeName}</td>
                                <td className="py-2 px-4 border-b text-center">
                                    {route.locations.length > 0 ? (
                                        <ul className="list-disc list-inside">
                                            {route.locations.map((loc, idx) => (
                                                <li key={idx}>
                                                    {loc.location}{' '}
                                                    <button
                                                        onClick={() => handleDeleteLocation(route._id, loc.location)}
                                                        className="ml-2 text-red-500 hover:text-red-700"
                                                    >
                                                        Delete
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <span>No locations</span>
                                    )}
                                </td>
                                <td className="py-2 px-4 border-b text-center">
                                    <button
                                        onClick={() => handleDeleteRoute(route._id)}
                                        className="bg-red-500 text-white px-4 py-1 rounded hover:bg-red-700"
                                    >
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default BusRoutePage;
